/* eslint-disable no-underscore-dangle */
import React, { useMemo, useCallback, useState, useRef } from 'react';
import classNames from 'classnames';
import { orderBy, findIndex, without, debounce } from 'lodash';
import { DIRECTION } from 'constants/config';
import Text, { TEXT_VARIANTS } from 'components/ui/Typography/Text';
import { Button } from 'ui';
import Choice from './Choice';

const DEBOUNCE_CHANGES_DELAY = 300;

const KnowledgeCheckSorting = ({
  choices: unorderedChoices,
  requestUpdateAndApplyChanges,
  applyChanges,
  handleUpdate,
}) => {
  const [loading, setLoading] = useState(false);
  const [disableAddItem, setDisableAddItem] = useState(false);
  const choices = useMemo(
    () => orderBy(unorderedChoices, 'position'),
    [unorderedChoices],
  );

  const debouncedHandleUpdate = useMemo(() => {
    const asyncUpdate = e => {
      handleUpdate(e).then(() => {
        setDisableAddItem(false);
      });
    };
    return debounce(asyncUpdate, DEBOUNCE_CHANGES_DELAY);
  }, [handleUpdate]);

  const [animatingIndexes, setAnimatingIndexes] = useState(null);
  const timer = useRef(false);
  const handleSwapAnimation = useCallback(change => {
    setAnimatingIndexes(change);
    if (timer.current) {
      clearTimeout(timer.current);
    }
    timer.current = true;
    setTimeout(() => {
      setAnimatingIndexes(null);
      timer.current = false;
    }, 300);
    return () => clearTimeout(timer.current);
  }, []);

  const swapArrayItems = (arr, index1, index2) => {
    const copy = [...arr];
    [copy[index1], copy[index2]] = [copy[index2], copy[index1]];
    return copy;
  };

  const reorder = useCallback(
    (direction, index) => {
      const reorderedChoices = swapArrayItems(
        orderBy(choices, 'position'),
        index,
        direction === DIRECTION.UP ? index - 1 : index + 1,
      );
      return reorderedChoices.map((c, i) => ({ ...c, position: i + 1 }));
    },
    [choices],
  );

  const onUpdateChoice = useCallback(
    choice => {
      setDisableAddItem(true);
      const params = {
        id: choice.id,
        text: choice.text,
        position: choice.position,
        choice_path: choice.choice_path,
      };

      const choicesCopy = [...choices];
      const choiceToUpdateIndex = findIndex(choices, ['id', choice.id]);

      if (choiceToUpdateIndex !== -1 && choice.id !== undefined) {
        choicesCopy.splice(choiceToUpdateIndex, 1, params);
      }

      applyChanges({ choices: choicesCopy }, { mergeChanges: false });
      debouncedHandleUpdate({ choices: choicesCopy });
    },
    [applyChanges, choices, debouncedHandleUpdate],
  );

  const onReorderChoices = useCallback(
    reorderedChoices => {
      setDisableAddItem(true);
      applyChanges({ choices: reorderedChoices }, { mergeChanges: false });
      debouncedHandleUpdate({ choices: reorderedChoices });
    },
    [applyChanges, debouncedHandleUpdate],
  );

  const addChoice = useCallback(() => {
    const params = {
      text: '',
      correct: true,
      position: choices.length + 1,
    };

    setLoading(true);
    requestUpdateAndApplyChanges({
      choices: [...choices, params],
    }).then(() => setLoading(false));
  }, [requestUpdateAndApplyChanges, choices]);

  const onRemoveChoice = useCallback(
    choice => {
      const choiceCopy = choice;
      delete choiceCopy.position;
      const choicesCopy = orderBy(without(choices, choice), 'position').map(
        (c, index) => ({
          ...c,
          position: index + 1,
        }),
      );
      setLoading(true);
      requestUpdateAndApplyChanges({
        choices: [...choicesCopy, { ...choiceCopy, _destroy: true }],
      }).then(() => setLoading(false));
    },
    [choices, requestUpdateAndApplyChanges],
  );

  const [hideAllGrabbers, setHideAllGrabbers] = useState(false);
  const moveChoice = useCallback(
    (dragIndex, hoverIndex) => {
      setDisableAddItem(true);
      setHideAllGrabbers(true);
      const reorderedChoices = swapArrayItems(
        choices,
        dragIndex,
        hoverIndex,
      ).map((c, i) => ({ ...c, position: i + 1 }));
      applyChanges(
        {
          choices: reorderedChoices,
        },
        { mergeChanges: false },
      );
    },
    [applyChanges, choices],
  );

  const onDrop = useCallback(
    e => {
      setHideAllGrabbers(false);
      debouncedHandleUpdate({
        choices,
      });
    },
    [choices, debouncedHandleUpdate, setHideAllGrabbers],
  );

  const renderChoice = useCallback(
    (choice, index) => {
      if (!choice.position) return null;
      return (
        <Choice
          choice={choice}
          reorder={direction => {
            if (direction === DIRECTION.UP) {
              handleSwapAnimation({
                movingUpIndex: index,
                movingDownIndex: index - 1,
              });
            } else {
              handleSwapAnimation({
                movingUpIndex: index + 1,
                movingDownIndex: index,
              });
            }
            const reorderedChoices = reorder(direction, index);
            onReorderChoices(reorderedChoices);
          }}
          onDelete={c => onRemoveChoice(c)}
          onUpdate={c => onUpdateChoice(c)}
          addChoice={() => {
            if (choices.length < 10) addChoice();
          }}
          key={choice.id}
          loading={loading}
          disableUpArrow={index === 0}
          disableDownArrow={choices?.length - 1 === index}
          index={index}
          moveChoice={moveChoice}
          onDrop={onDrop}
          hideAllGrabbers={hideAllGrabbers || choices.length === 1}
          disableAddItem={disableAddItem}
          className={classNames({
            // Determine animation classes based on the index AFTER they were reordered
            'SortingQuestion-Animation--translateUp':
              animatingIndexes?.movingUpIndex === index + 1,
            'SortingQuestion-Animation--translateDown':
              animatingIndexes?.movingDownIndex === index - 1,
          })}
        />
      );
    },
    [
      addChoice,
      loading,
      moveChoice,
      onDrop,
      onRemoveChoice,
      onReorderChoices,
      onUpdateChoice,
      reorder,
      choices.length,
      hideAllGrabbers,
      disableAddItem,
      handleSwapAnimation,
      animatingIndexes,
    ],
  );

  return (
    <div className="pb-32">
      <div className="pt-24">
        <Text variant={TEXT_VARIANTS.INSTRUX_SEMIBOLD}>
          {t('questions.choices.sorting.items_label')}
          {<span className="c-danger-red"> * </span>}
        </Text>
      </div>
      <div className="mb-8">
        <Text variant={TEXT_VARIANTS.INSTRUX_SMALL_SEMIBOLD}>
          {t('questions.choices.sorting.items_subtitle')}
        </Text>
      </div>
      <div
        className="InteractiveElement-marginLeft"
        data-testid="KnowledgeCheckSorting-choices"
      >
        {choices.map((choice, index) => renderChoice(choice, index))}
      </div>
      <Button
        icon="add"
        className={classNames('mt-8', {
          'ml-34': choices.length !== 0,
        })}
        tall
        onClick={() => {
          addChoice();
        }}
        disabled={choices.length >= 10}
        readOnly={loading || disableAddItem}
      >
        {t('questions.choices.sorting.add')}
      </Button>
    </div>
  );
};

export default KnowledgeCheckSorting;
